import { createAuditLog } from '../graphql/mutations';
import { enqueueSnackbar } from 'notistack';

/** `AuditLogType` value used for every scheduled bulk operation entry. */
export const SCHEDULED_BULK_OPERATION_AUDIT_TYPE = 'ScheduledBulkOperation';

/** Audit entries expire after six months, matching the Segment entries. */
const SIX_MONTHS_SECONDS = 6 * 30 * 24 * 60 * 60;

/**
 * Builds the CreateAuditLogInput for a scheduled bulk operation change.
 *
 * Pure: no clock, no client, no React. `now` is injectable so the timestamp and
 * ttl can be asserted directly.
 *
 * @param {object} params
 * @param {string} params.tenant       Signed-in user's tenant.
 * @param {string} params.userName     Signed-in user's email.
 * @param {string} params.documentId   Operation id.
 * @param {object} params.operation    Post-change operation state (ignored for DELETED).
 * @param {'CREATED'|'UPDATED'|'DELETED'} params.action
 * @param {number} [params.now]        Epoch ms, injectable for tests.
 * @returns {object} CreateAuditLogInput
 */
export function buildScheduleAuditInput({
	tenant,
	userName,
	documentId,
	operation,
	action,
	now = Date.now(),
}) {
	return {
		tenant,
		type: SCHEDULED_BULK_OPERATION_AUDIT_TYPE,
		timestamp: new Date(now).toISOString(),
		userName,
		documentId,
		name: operation?.name ?? null,
		// currentValue is AWSJSON, so it is always a JSON string. A deletion has
		// no post-change state to record, so it stores an empty object.
		currentValue:
			action === 'DELETED' ? '{}' : JSON.stringify(snapshot(operation)),
		action,
		ttl: Math.floor(now / 1000) + SIX_MONTHS_SECONDS,
	};
}

/**
 * The audited configuration snapshot.
 *
 * `qbQuery` is kept as the stored string rather than re-parsed and nested, so
 * the snapshot stays a flat, diffable record of exactly what is on the row.
 *
 * @param {object} operation Post-change operation state.
 * @returns {object} Snapshot of the whole configuration.
 */
function snapshot(operation) {
	return {
		name: operation?.name ?? null,
		description: operation?.description ?? null,
		action: operation?.action ?? null,
		schedule: operation?.schedule ?? null,
		// The resulting boolean, not the raw field: an absent value is enabled.
		enabled: operation?.enabled !== false,
		updateField: operation?.updateField ?? null,
		updateValue: operation?.updateValue ?? null,
		qbQuery: operation?.query?.qbQuery ?? null,
		sourceSegmentName: operation?.sourceSegmentName ?? null,
	};
}

/**
 * Writes the entry. Never rejects: an audit failure must not undo a
 * configuration change that already succeeded.
 *
 * @param {object} client AppSync client from `generateClient()`.
 * @param {object} input  CreateAuditLogInput from `buildScheduleAuditInput`.
 * @returns {Promise<boolean>} `true` when the entry was written.
 */
export async function writeScheduleAuditLog(client, input) {
	try {
		await client.graphql({ query: createAuditLog, variables: { input } });
		return true;
	} catch (err) {
		console.error(
			'Unable to write the scheduled bulk operation audit entry',
			err,
		);
		enqueueSnackbar(
			'The change was saved but could not be recorded in the audit log',
			{ variant: 'warning' },
		);
		return false;
	}
}
